/**
 * CaptureChip.js — one component, three variants.
 *
 *   variant='confirm'    — pending-state chip shown below a user chat bubble
 *                          when backend returns status='pending'. Three action
 *                          buttons: Confirm / Edit / Dismiss.
 *
 *   variant='captured'   — post-save (auto-capture or user confirm/edit) chip.
 *                          Subtle positive styling. Hover surfaces rationale.
 *                          Optional Retract action.
 *
 *   variant='provenance' — small pill under an AI answer indicating the
 *                          answer drew on a chat-captured assertion. Clickable
 *                          (deep-link to /captures?candidate_id=X).
 *
 * All variants show `label`. captured + provenance show `rationale` on hover
 * via the native title attribute (matches the app-wide lightweight tooltip
 * pattern — no custom tooltip primitive exists).
 *
 * No API calls inside the component. Callers pass handlers.
 */

import React from 'react';
import { colors } from './AppLayout';


function CaptureChip({
  variant,
  label,
  rationale = null,
  candidateId = null,
  onConfirm = null,
  onEdit = null,
  onDismiss = null,
  onRetract = null,
  onClick = null,
  disabled = false,
}) {
  // Shared container styles
  const baseContainer = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 'var(--font-size-xs, 12px)',
    lineHeight: 1.4,
  };

  // Shared action-button style (confirm variant)
  const actionBtn = {
    background: 'transparent',
    border: `1px solid ${colors.mediumGray}`,
    color: colors.darkText,
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: 'var(--font-size-xs, 12px)',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };

  const primaryActionBtn = {
    ...actionBtn,
    background: colors.electricBlue,
    border: `1px solid ${colors.electricBlue}`,
    color: colors.white,
  };

  if (variant === 'confirm') {
    return (
      <div
        title={rationale || undefined}
        data-candidate-id={candidateId ?? undefined}
        style={{
          ...baseContainer,
          padding: '8px 12px',
          border: `1px dashed ${colors.electricBlue}`,
          borderRadius: '8px',
          backgroundColor: '#F5F8FF',
          color: colors.darkText,
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600, marginRight: '6px' }}>Capture this?</span>
          {label}
        </span>
        {disabled ? (
          <span style={{ color: colors.textMuted, fontStyle: 'italic' }}>
            Already saved
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={onConfirm || undefined}
              disabled={disabled || !onConfirm}
              style={primaryActionBtn}
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={onEdit || undefined}
              disabled={disabled || !onEdit}
              style={actionBtn}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDismiss || undefined}
              disabled={disabled || !onDismiss}
              style={actionBtn}
            >
              Dismiss
            </button>
          </>
        )}
      </div>
    );
  }

  if (variant === 'captured') {
    return (
      <div
        title={rationale || undefined}
        data-candidate-id={candidateId ?? undefined}
        style={{
          ...baseContainer,
          padding: '6px 10px',
          border: `1px solid ${colors.success}`,
          borderRadius: '6px',
          backgroundColor: '#ECFDF5',
          color: '#065F46',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="#065F46" strokeWidth="2.5" strokeLinecap="round"
             strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
        {onRetract && (
          <button
            type="button"
            onClick={onRetract}
            disabled={disabled}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#065F46',
              padding: '2px 6px',
              fontSize: 'var(--font-size-xs, 12px)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              textDecoration: 'underline',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            Retract
          </button>
        )}
      </div>
    );
  }

  if (variant === 'provenance') {
    const clickable = Boolean(onClick) && !disabled;
    return (
      <button
        type="button"
        title={rationale || undefined}
        data-candidate-id={candidateId ?? undefined}
        onClick={clickable ? onClick : undefined}
        disabled={!clickable}
        style={{
          ...baseContainer,
          padding: '3px 10px',
          border: `1px solid ${colors.mediumGray}`,
          borderRadius: '100px',
          backgroundColor: colors.white,
          color: colors.textMuted,
          cursor: clickable ? 'pointer' : 'default',
          fontSize: '11px',
          fontWeight: 500,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
             stroke={colors.textMuted} strokeWidth="2" strokeLinecap="round"
             strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span>{label}</span>
      </button>
    );
  }

  // Unknown variant — render nothing rather than crashing. Surface a console
  // warning so the caller notices.
  console.warn(`[CaptureChip] unknown variant: ${variant}`);
  return null;
}

export default CaptureChip;
